import { TruncatedTooltip } from '@common/components/TruncatedTooltip';
import { useSelectedClip } from '@features/Clip';
import { Box, Button, Flex, Popover, Separator, Text } from '@radix-ui/themes';
import { useMediaPlayer, useMediaState } from '@vidstack/react';
import classNames from 'classnames';
import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCustomTrackName } from '../utils/trackName';
import { SolidPopoverContent } from './SmartPlayerUI';

import styles from '../styles/SmartPlayer.module.css';

export const SmartPlayerAudioTrackSelector: React.FC = () => {
    const { t } = useTranslation('smartPlayer');
    const player = useMediaPlayer();

    // Media State
    const audioTracks = useMediaState('audioTracks');
    const activeAudioTrack = useMediaState('audioTrack');

    // Proxy Data
    const { selectedClip } = useSelectedClip();
    const audioLayouts = useMemo(
        () => selectedClip?.audioLayouts || [],
        [selectedClip?.audioLayouts],
    );

    // Derived State
    const tracks = useMemo(
        () => (audioTracks ? Array.from(audioTracks) : []),
        [audioTracks],
    );

    const activeTrack =
        activeAudioTrack || tracks.find((t) => t.selected) || tracks[0];

    // Safe fallback generator
    const getFallback = useCallback(
        (id: string) => t('audio.track_fallback', { id }),
        [t],
    );

    const activeTrackShortName = useMemo(() => {
        if (!activeTrack) return '';
        const activeTrackIndex = tracks.findIndex(
            (t) => t.id === activeTrack.id,
        );

        return formatCustomTrackName({
            track: activeTrack,
            index: activeTrackIndex,
            allTracks: tracks,
            layout: audioLayouts[activeTrackIndex],
            isShort: true,
            getFallback,
        });
    }, [activeTrack, tracks, audioLayouts, getFallback]);

    if (tracks.length <= 1) return null;

    return (
        <Popover.Root>
            <Popover.Trigger>
                <Button
                    variant='ghost'
                    color='gray'
                    highContrast
                    size='2'
                    className={styles.trackSelectorTrigger}
                >
                    <Box
                        style={{ minWidth: 0, flexShrink: 1, fontWeight: 500 }}
                    >
                        <TruncatedTooltip>
                            {t('audio.language')} {activeTrackShortName}
                        </TruncatedTooltip>
                    </Box>
                </Button>
            </Popover.Trigger>

            <SolidPopoverContent
                container={player?.el || undefined}
                style={{ width: '220px', padding: 'var(--space-3)' }}
            >
                <Box mb='2'>
                    <Text
                        size='1'
                        weight='bold'
                        color='gray'
                        className={styles.menuHeader}
                    >
                        {t('audio.tracks_title')}
                    </Text>
                </Box>
                <Separator size='4' mb='3' style={{ opacity: 0.15 }} />

                <Flex direction='column' gap='1'>
                    {tracks.map((track, index) => {
                        const displayName = formatCustomTrackName({
                            track,
                            index,
                            allTracks: tracks,
                            layout: audioLayouts[index],
                            isShort: false,
                            getFallback,
                        });

                        const isSelected = activeTrack?.id === track.id;

                        return (
                            <Button
                                key={track.id}
                                variant='ghost'
                                color='gray'
                                onClick={() => {
                                    track.selected = true;
                                }}
                                className={styles.menuItem}
                            >
                                <Box
                                    className={classNames(
                                        styles.checkContainer,
                                        { [styles.checkVisible]: isSelected },
                                    )}
                                >
                                    ✓
                                </Box>
                                <Box className={styles.truncateContainer}>
                                    <TruncatedTooltip>
                                        {displayName}
                                    </TruncatedTooltip>
                                </Box>
                            </Button>
                        );
                    })}
                </Flex>
            </SolidPopoverContent>
        </Popover.Root>
    );
};
